'use client';

import { memo, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow
} from '@repo/ui/components/table';
import { TableShellHead } from '@repo/ui/components/table-shell';
import { cn } from '@repo/ui/lib/utils';
import type { BarLineChartDataPoint } from '@repo/ui/components/bar-line-chart';
import { BarLineChart } from '@/components/lazy-charts';

import type { InvestorPnlResponse } from '../types/api';
import { formatMonth } from '@/lib/format/date-time/day';
import { formatBasisPoints } from '@/lib/format/percent/basis-points';
import { formatSignedCompactUsd } from '@/lib/format/usd/signed-compact-usd';
import { formatUsdFigure } from '@/lib/format/usd/usd-figure';
import {
  ErrorBlock,
  LIVE_BADGE,
  LoadingBlock,
  SectionShell
} from './fees-and-capital-section';

interface CapitalAccountProps {
  pnl?: InvestorPnlResponse;
  isLoading?: boolean;
  isError?: boolean;
  periodLabel: string;
}

const VISIBLE_MONTHS = 12;

/** A served 2 decimal USD figure, compact with up to two places. */
const USD_2 = { maximumFractionDigits: 2 } as const;

const FLOW_BARS = [
  { key: 'deposits', name: 'Deposits', color: 'var(--color-rowa-chart-2)' },
  {
    key: 'withdrawals',
    name: 'Withdrawals',
    color: 'var(--color-rowa-chart-5)'
  }
];
const FLOW_LINES = [
  { key: 'net', name: 'Net flow', color: 'var(--color-rowa-text-primary)' }
];
/** Chart axis and tooltip money: compact, with a true minus below zero. */
const formatFlowAxis = (value: number) =>
  formatSignedCompactUsd(value, { maximumFractionDigits: 1 });
const formatFlowValue = (value: number) =>
  formatSignedCompactUsd(value, { maximumFractionDigits: 2 });

/** A served signed 2 decimal USD string: "+$12.3K", "−$450". */
const signedUsd = (value: string) =>
  formatSignedCompactUsd(Number(value), {
    maximumFractionDigits: 2,
    plus: true
  });

/**
 * The flows chart behind a memo boundary with constant series and
 * formatters: the card re-renders on every query update on the page, and
 * the chart should only redraw when its own months change.
 */
const FlowsChart = memo(function FlowsChart({
  data
}: {
  data: BarLineChartDataPoint[];
}) {
  return (
    <BarLineChart
      data={data}
      bars={FLOW_BARS}
      lines={FLOW_LINES}
      height={200}
      yTickFormatter={formatFlowAxis}
      valueFormatter={formatFlowValue}
    />
  );
});

/**
 * Capital account (spec 1.5): the roll forward the backend serves — opening
 * + deposits − withdrawals + P&L = closing, verified at the parse boundary
 * to within the cent that rounding each figure on its own can leave. Window
 * totals up top, months beneath (latest first, folded past a year).
 */
export function CapitalAccount({
  pnl,
  isLoading = false,
  isError = false,
  periodLabel
}: CapitalAccountProps) {
  const [showAllMonths, setShowAllMonths] = useState(false);

  const months = [...(pnl?.months ?? [])].reverse();
  const visibleMonths = showAllMonths
    ? months
    : months.slice(0, VISIBLE_MONTHS);
  const hiddenCount = months.length - visibleMonths.length;

  // Chronological for the chart: deposits rise above zero, withdrawals
  // hang below it, and the net flow threads through — the rhythm of money
  // moving that the table's columns cannot show at a glance.
  const servedMonths = pnl?.months;
  const flowChartData = useMemo<BarLineChartDataPoint[]>(
    () =>
      (servedMonths ?? []).map((month) => ({
        label: formatMonth(month.month),
        deposits: Number(month.contributionsUsd),
        withdrawals: -Number(month.withdrawalsUsd),
        net: Number(month.netFlowUsd)
      })),
    [servedMonths]
  );

  return (
    <SectionShell
      titleId="capital-account-title"
      title="Capital account"
      note={`${periodLabel} · opening value + deposits − withdrawals + profit and loss = closing value`}
      badge={pnl ? LIVE_BADGE : undefined}
    >
      {isLoading ? (
        <LoadingBlock />
      ) : isError || !pnl ? (
        <ErrorBlock />
      ) : (
        <>
          <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[
              {
                label: 'Opening',
                value: formatUsdFigure(pnl.openingValueUsd, USD_2)
              },
              {
                label: 'Deposits',
                value: formatUsdFigure(pnl.contributionsUsd, USD_2)
              },
              {
                label: 'Withdrawals',
                value: formatUsdFigure(pnl.withdrawalsUsd, USD_2)
              },
              {
                label: 'Net P&L',
                value: signedUsd(pnl.netPnlUsd),
                negative: Number(pnl.netPnlUsd) < 0
              },
              {
                label: 'Closing',
                value: formatUsdFigure(pnl.closingValueUsd, USD_2)
              }
            ].map((stat) => (
              <div
                key={stat.label}
                className="bg-rowa-surface-inset rounded-rowa-md px-3 py-2.5"
              >
                <dt className="text-rowa-label-sm text-rowa-text-tertiary tracking-[0.08em] uppercase">
                  {stat.label}
                </dt>
                <dd
                  className={cn(
                    'text-rowa-data-medium mt-1 tabular-nums',
                    stat.negative
                      ? 'text-rowa-status-error'
                      : 'text-rowa-text-primary'
                  )}
                >
                  {stat.value}
                </dd>
              </div>
            ))}
          </dl>

          {flowChartData.length > 1 ? (
            <div className="mt-5">
              <FlowsChart data={flowChartData} />
            </div>
          ) : null}

          {months.length === 0 ? (
            <p className="text-rowa-body-sm text-rowa-text-secondary mt-6">
              Month by month history appears after your first deposit.
            </p>
          ) : (
            <div className="mt-5 min-w-0 overflow-x-auto [&_tr]:border-none">
              <Table aria-label="Capital account by month">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableShellHead className="pl-0">Month</TableShellHead>
                    <TableShellHead align="right">In</TableShellHead>
                    <TableShellHead align="right">Out</TableShellHead>
                    <TableShellHead align="right">P&L</TableShellHead>
                    <TableShellHead align="right">Return</TableShellHead>
                    <TableShellHead align="right" className="pr-0">
                      Closing
                    </TableShellHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleMonths.map((month) => (
                    <TableRow
                      key={month.month}
                      className="hover:bg-transparent"
                    >
                      <TableCell className="text-rowa-body-sm text-rowa-text-primary py-2.5 pl-0 font-medium">
                        {formatMonth(month.month)}
                      </TableCell>
                      <TableCell className="text-rowa-body-sm text-rowa-text-secondary text-right font-mono tabular-nums">
                        {formatUsdFigure(month.contributionsUsd, USD_2)}
                      </TableCell>
                      <TableCell className="text-rowa-body-sm text-rowa-text-secondary text-right font-mono tabular-nums">
                        {formatUsdFigure(month.withdrawalsUsd, USD_2)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'text-rowa-body-sm text-right font-mono tabular-nums',
                          Number(month.netPnlUsd) < 0
                            ? 'text-rowa-status-error'
                            : 'text-rowa-text-primary'
                        )}
                      >
                        {signedUsd(month.netPnlUsd)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'text-rowa-body-sm text-right font-mono tabular-nums',
                          month.returnBps != null && month.returnBps < 0
                            ? 'text-rowa-status-error'
                            : 'text-rowa-text-secondary'
                        )}
                      >
                        {month.returnBps == null
                          ? '—'
                          : formatBasisPoints(month.returnBps, {
                              signed: true,
                              fractionDigits: 1
                            })}
                      </TableCell>
                      <TableCell className="text-rowa-body-sm text-rowa-text-primary py-2.5 pr-0 text-right font-mono tabular-nums">
                        {formatUsdFigure(month.closingValueUsd, USD_2)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {hiddenCount > 0 || showAllMonths ? (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={6} className="py-2 pl-0">
                        <button
                          type="button"
                          className="text-rowa-label-md text-rowa-text-brand font-medium hover:opacity-80"
                          onClick={() => setShowAllMonths((value) => !value)}
                        >
                          {showAllMonths
                            ? 'Show the last year only'
                            : `Show all ${months.length} months`}
                        </button>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="border-rowa-border-default text-rowa-label-sm text-rowa-text-tertiary mt-auto space-y-1 border-t pt-3 [&>*:first-child]:mt-4">
            <p className="mt-4">
              Deposits and withdrawals include share transfers in and out,
              valued at the share price at the time.
            </p>
            {pnl.monthsTruncated ? (
              <p>
                Showing the most recent 60 months of {pnl.monthsTotal}. The
                totals above always cover the whole window.
              </p>
            ) : null}
          </div>
        </>
      )}
    </SectionShell>
  );
}
